import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Loader2 } from 'lucide-react';
import { LeadReportEmail } from './LeadReportEmail';
import { HelpLink } from './HelpLink';
import { TopLeadPages } from './TopLeadPages';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const n = (v) => (v ?? 0).toLocaleString();
const pct = (v) => (v == null ? '—' : `${v}%`);
const top = (t) => (t ? `${t.title || t.cta} (${t.count})` : '—');

// Small month-over-month badge; counts show +n (+x%), rates show +x pts.
const Delta = ({ d, id }) => {
  if (!d) return <span className="block text-[11px] text-neutral-400" data-testid={`lead-delta-${id}`}>vs prev: n/a</span>;
  const v = d.change_pts ?? d.change;
  const txt = d.change_pts != null ? `${v > 0 ? '+' : ''}${v} pts` : `${v > 0 ? '+' : ''}${v}${d.change_pct != null ? ` (${d.change_pct > 0 ? '+' : ''}${d.change_pct}%)` : ''}`;
  return <span className={`block text-[11px] ${v > 0 ? 'text-green-700' : v < 0 ? 'text-red-700' : 'text-neutral-400'}`} title={`Previous: ${d.previous}${d.change_pts != null ? '%' : ''}`} data-testid={`lead-delta-${id}`}>{txt} vs prev</span>;
};

const Stat = ({ label, value, id, strong, wrap, delta, deltaId }) => (
  <div className={`border rounded-lg px-4 py-3 min-w-0 ${strong ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white border-neutral-200'}`}>
    <p className={`text-xs ${strong ? 'text-neutral-300' : 'text-neutral-500'}`}>{label}</p>
    <p className={`font-semibold ${wrap ? 'text-base break-words line-clamp-3' : 'text-lg truncate'}`} title={String(value)} data-testid={`lead-${id}`}>{value}</p>
    {deltaId && <Delta d={delta} id={deltaId} />}
  </div>
);

const Table = ({ head, rows, testId }) => (
  <div className="bg-white border border-neutral-200 rounded-lg overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead className="bg-neutral-50 text-xs text-neutral-500"><tr>{head.map((h, i) => <th key={h} className={`px-3 py-2 ${i ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr></thead>
      <tbody>{rows.length ? rows.map((r) => (
        <tr key={r.key} className="border-t border-neutral-100">{r.cells.map((c, i) => <td key={i} className={`px-3 py-2 ${i ? 'text-right tabular-nums' : ''}`}>{c}</td>)}</tr>
      )) : <tr><td colSpan={head.length} className="px-3 py-6 text-center text-neutral-400">No data this month.</td></tr>}</tbody>
    </table>
  </div>
);

const SrcCell = ({ s }) => <>{s.title}{s.path && <span className="block text-xs text-neutral-400">{s.path}</span>}</>;

const Consultations = ({ c, cmp }) => (
  <section className="space-y-3" data-testid="lead-report-consultations">
    <div><h3 className="text-lg font-serif">Consultations</h3><p className="text-xs text-neutral-500">Converted = Confirmed or Completed (current status).</p></div>
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
      <Stat id="c-total" label="Total" value={n(c.total)} delta={cmp.consultations} deltaId="consultations" /><Stat id="c-confirmed" label="Confirmed" value={n(c.confirmed)} /><Stat id="c-completed" label="Completed" value={n(c.completed)} />
      <Stat id="c-converted" label="Converted total" value={n(c.converted)} /><Stat id="c-pct" label="Conversion" value={pct(c.conversion_pct)} delta={cmp.consultation_conversion} deltaId="consultation-conversion" />
      <Stat id="c-top-source" label="Top source page" value={top(c.top_source)} wrap /><Stat id="c-top-cta" label="Top CTA / button" value={top(c.top_cta)} wrap />
      <Stat id="c-direct" label="Direct visits" value={n(c.direct)} /><Stat id="c-none" label="Source not recorded" value={n(c.not_recorded)} />
    </div>
    <Table testId="lead-report-c-by-source" head={['Source page', 'Total', 'Confirmed', 'Completed', 'Converted', 'Conversion']}
      rows={c.by_source.map((s) => ({ key: s.key, cells: [<SrcCell s={s} />, s.count, s.confirmed, s.completed, s.converted, pct(s.conversion_pct)] }))} />
  </section>
);

const chSub = (ch) => (ch.conversion_trackable ? `${ch.converted}/${ch.trackable} converted` : 'conversion not trackable');

const Quotes = ({ q, cmp }) => (
  <section className="space-y-3" data-testid="lead-report-quotes">
    <div><h3 className="text-lg font-serif flex items-center gap-2">Quote Requests <HelpLink topic="conversion-reporting" /></h3><p className="text-xs text-neutral-500" data-testid="lead-q-definition">{q.conversion_definition} Conversion % uses trackable requests only.</p></div>
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
      <Stat id="q-total" label="Total" value={n(q.total)} delta={cmp.quotes} deltaId="quotes" />
      <Stat id="q-trackable" label="Conversion-trackable" value={n(q.conversion.trackable)} />
      <Stat id="q-converted" label="Converted" value={n(q.conversion.converted)} />
      <Stat id="q-pct" label="Conversion (trackable)" value={pct(q.conversion.conversion_pct)} delta={cmp.quote_conversion} deltaId="quote-conversion" />
      <Stat id="q-not-trackable" label="Not trackable" value={n(q.conversion.not_trackable)} />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
      {q.by_channel.map((ch) => <Stat key={ch.channel} id={`q-ch-${ch.channel}`} label={`${ch.label} · ${chSub(ch)}`} value={n(ch.count)} />)}
      <Stat id="q-top-source" label="Top source page" value={top(q.top_source)} wrap /><Stat id="q-top-cta" label="Top CTA / button" value={top(q.top_cta)} wrap />
      <Stat id="q-direct" label="Direct / Quote Page" value={n(q.direct)} /><Stat id="q-none" label="Source not recorded" value={n(q.not_recorded)} />
    </div>
    <Table testId="lead-report-q-by-source" head={['Source page', 'Requests', 'Trackable', 'Converted', 'Conversion']}
      rows={q.by_source.map((s) => ({ key: s.key, cells: [<SrcCell s={s} />, s.count, s.trackable, s.converted, s.trackable ? pct(s.conversion_pct) : 'n/a'] }))} />
  </section>
);

// Monthly report with separate consultation and quote sections (calendar months, New York time).
export const LeadReportTab = ({ token }) => {
  const [month, setMonth] = useState('');
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [retry, setRetry] = useState(0);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  useEffect(() => {
    setState('loading');
    axios.get(`${API_URL}/admin/lead-report`, { ...auth, params: { month: month || undefined } })
      .then((r) => { setData(r.data); setState('ok'); }).catch(() => { setState('error'); toast.error('Could not load lead report'); });
  }, [token, month, retry]); // eslint-disable-line react-hooks/exhaustive-deps
  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/lead-report/export`, { ...auth, params: { month: month || data.month }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = Object.assign(document.createElement('a'), { href: url, download: `lead_report_${month || data.month}.csv` });
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to export lead report'); }
  };
  if (!data && state === 'error') return <div className="py-16 text-center text-sm text-neutral-500" data-testid="lead-report-error">Could not load the lead report. <button type="button" className="underline" onClick={() => setRetry((x) => x + 1)} data-testid="lead-report-retry">Try again</button></div>;
  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>;
  return (
    <div className="space-y-8" data-testid="lead-report-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="text-xl font-serif flex items-center gap-2">Monthly Lead Report — {data.label}{data.is_current && <span className="text-neutral-400 text-base"> (so far)</span>} <HelpLink topic="lead-report" /></h2>
          <p className="text-sm text-neutral-500">Calendar month, New York time ({data.date_from} to {data.through ? `${data.through}, month-to-date` : data.date_to}). Built from existing consultation and quote source tracking.</p></div>
        <div className="flex items-center gap-2">
          {state === 'loading' && <Loader2 className="w-4 h-4 animate-spin text-neutral-400" data-testid="lead-report-loading" />}
          {state === 'error' && <button type="button" className="text-xs text-red-700 underline" onClick={() => setRetry((x) => x + 1)} data-testid="lead-report-retry">Retry</button>}
          <select className="h-9 border border-neutral-300 bg-white px-2 text-sm rounded" value={month || data.month} onChange={(e) => setMonth(e.target.value)} data-testid="lead-report-month">
            {data.months.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
          <button type="button" onClick={exportCsv} className="inline-flex items-center gap-1.5 text-sm border border-neutral-300 rounded px-3 h-9 hover:border-neutral-900" data-testid="lead-report-export"><Download className="w-4 h-4" />Export CSV</button>
        </div>
      </div>
      <div className={`space-y-8 transition-opacity ${state === 'loading' ? 'opacity-50' : ''}`}>
      <LeadReportEmail token={token} month={data.month} isCurrent={data.is_current} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Stat strong id="combined" label="Combined leads (consultations + quote requests)" value={n(data.combined_leads)} delta={data.comparison.combined_leads} deltaId="combined" />
        <Stat id="combined-c" label="Consultations" value={n(data.consultations.total)} /><Stat id="combined-q" label="Quote requests" value={n(data.quotes.total)} />
      </div>
      <p className="text-xs text-neutral-500 -mt-5" data-testid="lead-compare-basis">Changes compare {data.comparison.current_label} with {data.comparison.previous_label}{data.comparison.basis === 'month_to_date' ? ' (same elapsed period of the previous month)' : ''}.</p>
      <TopLeadPages pages={data.top_pages || []} token={token} month={data.month} />
      <div className="border-t border-neutral-200" />
      <Consultations c={data.consultations} cmp={data.comparison} />
      <div className="border-t border-neutral-200" />
      <Quotes q={data.quotes} cmp={data.comparison} />
      </div>
    </div>
  );
};
